import { useEffect, useRef, useState } from 'react';
import { X, Building2, Users, Key, Search, Trash2, Link } from '../../icons';
import { Button, Checkbox, FormField, Select } from '../../components/shared';
import {
  listShares, addShare, removeShare,
  type NodePublic, type ShareEntry, type ShareTargetType, type SharePermission, type SharesResponse,
} from '../../api/workspace';
import { searchUsers, type UserSummary } from '../../api/users';
import { listAllTeams } from '../../api/organization';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 공유 · 액세스 관리 모달 (2026-08-14) — 사용자 요구:
 *
 *   "공유는 회사 전체 직원에게 공유, 팀공유가 가능해야하며, 각 팝업에 기능들을 구현하자."
 *
 * 공유 걸기와 액세스 관리(현재 공유 목록 보기·해제)를 **한 모달**로 합쳤다. 요구에는 두
 * 메뉴로 나와 있지만 화면이 갈라지면 "공유했는데 어디서 푸나" 를 매번 찾게 된다. 행 메뉴의
 * 두 항목은 같은 모달을 열되 초기 탭만 다르다.
 *
 * 대상 3종은 서로 다른 입력을 요구한다 — 전사는 대상 선택이 없고, 팀은 팀 목록에서,
 * 개인은 이름 검색으로 고른다.
 */

type Tab = 'add' | 'manage';

interface Props {
  node: NodePublic;
  /** 'access' 로 열면 관리 탭부터 보여 준다. */
  initialTab?: Tab;
  onClose: () => void;
  onChanged: () => void;
}

interface TeamOption { id: string; name: string }

export function WorkspaceShareModal({ node, initialTab = 'add', onClose, onChanged }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [targetType, setTargetType] = useState<ShareTargetType>('company');
  const [permission, setPermission] = useState<SharePermission>('VIEW');
  /**
   * 「하위 폴더」 (2026-10-27 사용자 지시) — 폴더 공유가 하위 폴더 안의 파일까지 닿는가.
   * 기본은 켬 — 지금까지 폴더 공유는 늘 아래가 전부 따라왔다(서버 기본값도 true). 파일에는 안 보인다.
   */
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const isFolder = node.type === 'folder';
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamId, setTeamId] = useState<string>('');
  const [userQuery, setUserQuery] = useState('');
  const [userHits, setUserHits] = useState<UserSummary[]>([]);
  const [pickedUser, setPickedUser] = useState<UserSummary | null>(null);
  const [shares, setShares] = useState<ShareEntry[]>([]);
  /** 상위 폴더에서 물려받은 공유 (2026-10-27) — 여기서는 보여 주기만 한다. */
  const [inherited, setInherited] = useState<NonNullable<SharesResponse['inherited']>>([]);
  const [canManage, setCanManage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEscapeClose(onClose, busy);

  /**
   * 이미 걸린 공유로 입력을 채운다 (2026-10-28 사용자 지시 「설정된 팀공유로 안 나오고 회사 전체가 기본으로 나온다」).
   * 처음 한 번만 — 사람이 고르기 시작한 뒤에 덮으면 안 된다. 직접 공유가 먼저, 없으면 물려받은 공유.
   */
  const prefilledRef = useRef(false);
  const prefillFrom = (res: SharesResponse) => {
    if (prefilledRef.current) return;
    prefilledRef.current = true;
    const s = res.shares[0] ?? res.inherited?.[0];
    if (!s) return;
    setTargetType(s.targetType);
    if (s.targetType === 'team') setTeamId(s.targetId);
    if (s.targetType === 'user') {
      setPickedUser({
        id: s.targetId, name: s.targetName ?? '', email: '', position: null, avatarUrl: null, role: '', status: '',
      });
    }
    setPermission(s.permission);
    if (s.includeSubfolders !== undefined) setIncludeSubfolders(s.includeSubfolders);
  };

  const reload = async () => {
    try {
      const res = await listShares(node.id);
      setShares(res.shares);
      setInherited(res.inherited ?? []);
      setCanManage(res.canManage);
      prefillFrom(res);
    } catch (err) {
      setError((err as any)?.response?.data?.message ?? (err as Error).message);
    }
  };

  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [node.id]);

  // 팀 목록은 팀 공유를 고른 순간에만 받는다 — 모달을 열 때마다 부르면 대부분 쓰이지 않는다.
  useEffect(() => {
    if (targetType !== 'team' || teams.length > 0) return;
    void (async () => {
      try {
        const all = await listAllTeams();
        setTeams(all.map((t) => ({ id: t.id, name: t.name })));
      } catch { /* 팀을 못 받아도 전사·개인 공유는 되어야 한다 */ }
    })();
  }, [targetType, teams.length]);

  // 이름 검색 — 입력마다 부르면 rate-limit 에 걸린다. 300ms 디바운스.
  useEffect(() => {
    if (targetType !== 'user') return;
    const q = userQuery.trim();
    if (q === '') { setUserHits([]); return; }
    const id = window.setTimeout(() => {
      void searchUsers(q).then(setUserHits).catch(() => setUserHits([]));
    }, 300);
    return () => window.clearTimeout(id);
  }, [userQuery, targetType]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (targetType === 'team' && teamId === '') {
      setError(tWorkspace('workspace.share.err_pick_team'));
      return;
    }
    if (targetType === 'user' && !pickedUser) {
      setError(tWorkspace('workspace.share.err_pick_user'));
      return;
    }
    setBusy(true);
    try {
      await addShare(node.id, {
        targetType,
        targetId: targetType === 'team' ? teamId : targetType === 'user' ? pickedUser!.id : undefined,
        permission,
        ...(isFolder ? { includeSubfolders } : {}),
      });
      setPickedUser(null);
      setUserQuery('');
      await reload();
      setTab('manage');
      onChanged();
    } catch (err) {
      setError((err as any)?.response?.data?.message ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(s: ShareEntry) {
    setBusy(true);
    setError(null);
    try {
      await removeShare(node.id, s.targetType, s.targetId);
      await reload();
      onChanged();
    } catch (err) {
      setError((err as any)?.response?.data?.message ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    const url = `${window.location.origin}/workspace?file=${node.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setNotice(tWorkspace('workspace.share.link_copied'));
      window.setTimeout(() => setNotice(null), 2000);
    } catch {
      // 클립보드 권한이 없을 수 있다 — 링크를 그대로 보여 주어 직접 복사하게 한다.
      setNotice(url);
    }
  }

  const scopeBtn = (type: ShareTargetType, Icon: typeof Building2, titleKey: string, descKey: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={targetType === type}
      className="ws-scope"
      onClick={() => setTargetType(type)}
    >
      <Icon size={18} aria-hidden />
      <span className="ws-scope-txt">
        <b>{tWorkspace(titleKey as never)}</b>
        <span>{tWorkspace(descKey as never)}</span>
      </span>
    </button>
  );

  return (
    <div
      className="workspace-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace('workspace.share.aria')}
    >
      <div ref={trapRef} className="workspace-modal ws-share-modal">
        <header className="workspace-modal-header">
          <h2>{tWorkspace('workspace.share.heading', { name: node.name })}</h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onClose} disabled={busy} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>

        <div className="ws-share-tabs" role="tablist">
          <button
            type="button" role="tab" aria-selected={tab === 'add'}
            className={`ws-share-tab${tab === 'add' ? ' ws-share-tab--on' : ''}`}
            onClick={() => setTab('add')}
          >
            {tWorkspace('workspace.share.tab_add')}
          </button>
          <button
            type="button" role="tab" aria-selected={tab === 'manage'}
            className={`ws-share-tab${tab === 'manage' ? ' ws-share-tab--on' : ''}`}
            onClick={() => setTab('manage')}
          >
            {tWorkspace('workspace.share.tab_manage', { count: shares.length + inherited.length })}
          </button>
        </div>

        {tab === 'add' ? (
          <form className="workspace-modal-body" onSubmit={handleAdd}>
            <div className="workspace-modal-field">
              <label>{tWorkspace('workspace.share.scope_label')}</label>
              <div className="ws-scope-list" role="radiogroup">
                {scopeBtn('company', Building2, 'workspace.share.scope_company', 'workspace.share.scope_company_desc')}
                {scopeBtn('team', Users, 'workspace.share.scope_team', 'workspace.share.scope_team_desc')}
                {scopeBtn('user', Key, 'workspace.share.scope_user', 'workspace.share.scope_user_desc')}
              </div>
            </div>

            {targetType === 'team' && (
              <FormField className="workspace-modal-field" htmlFor="ws-share-team"
                label={tWorkspace('workspace.share.pick_team')}>
                <Select
                  value={teamId === '' ? null : teamId}
                  placeholder={tWorkspace('workspace.share.pick_team_placeholder')}
                  options={teams.map((t) => ({ value: t.id, label: t.name }))}
                  onChange={setTeamId}
                  disabled={busy}
                  /* ★ 모달 본문은 overflow 상자다 — 안 켜면 목록이 잘린다. */
                  portal
                />
              </FormField>
            )}

            {targetType === 'user' && (
              <div className="workspace-modal-field">
                <label htmlFor="ws-share-user">{tWorkspace('workspace.share.pick_user')}</label>
                <div className="ws-user-search">
                  <Search size={14} aria-hidden />
                  <input
                    id="ws-share-user"
                    type="search"
                    value={pickedUser ? pickedUser.name : userQuery}
                    placeholder={tWorkspace('workspace.share.pick_user_placeholder')}
                    disabled={busy}
                    onChange={(e) => { setPickedUser(null); setUserQuery(e.target.value); }}
                  />
                </div>
                {!pickedUser && userHits.length > 0 && (
                  <ul className="ws-user-hits">
                    {userHits.slice(0, 8).map((u) => (
                      <li key={u.id}>
                        <button type="button" onClick={() => { setPickedUser(u); setUserHits([]); }}>
                          {u.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <FormField className="workspace-modal-field" htmlFor="ws-share-perm"
              label={tWorkspace('workspace.share.perm_label')}>
              <Select
                value={permission}
                options={[
                  { value: 'VIEW', label: tWorkspace('workspace.share.perm_view') },
                  { value: 'EDIT', label: tWorkspace('workspace.share.perm_edit') },
                ]}
                onChange={(v) => setPermission(v as SharePermission)}
                disabled={busy}
                portal
              />
            </FormField>

            {error && <div className="workspace-modal-error" role="alert">{error}</div>}
            {notice && <div className="workspace-upload-done" role="status">{notice}</div>}

            <footer className="workspace-modal-footer">
              {/* 「링크 복사」 앞 — 폴더일 때만(2026-10-27 사용자 지시). 왼쪽 끝에 붙인다. */}
              {isFolder && (
                <span className="ws-share-subfolders">
                  <Checkbox
                    checked={includeSubfolders}
                    disabled={busy}
                    onChange={(e) => setIncludeSubfolders(e.target.checked)}
                    label={tWorkspace('workspace.share.include_subfolders')}
                  />
                </span>
              )}
              <Button variant="secondary"
                onClick={() => void copyLink()} disabled={busy}
                iconLeft={<Link size={14} aria-hidden />}
              >
                {tWorkspace('workspace.row.copy_link')}
              </Button>
              <Button variant="primary" type="submit"
                disabled={busy}
              >
                {busy ? tWorkspace('workspace.share.btn_busy') : tWorkspace('workspace.share.btn_add')}
              </Button>
            </footer>
          </form>
        ) : (
          <div className="workspace-modal-body">
            {shares.length === 0 && inherited.length === 0 && (
              <p className="ws-detail-note">{tWorkspace('workspace.detail.no_shares')}</p>
            )}
            <ul className="ws-share-list">
              {shares.map((s) => (
                <li key={s.id} className="ws-share-row">
                  <span className="ws-avatar">
                    {s.targetType === 'company'
                      ? <Building2 size={13} aria-hidden />
                      : s.targetType === 'team'
                        ? <Users size={13} aria-hidden />
                        : <Key size={13} aria-hidden />}
                  </span>
                  <span className="ws-who-txt">
                    <b>{s.targetName ?? tWorkspace('workspace.detail.unknown_target')}</b>
                    <span>
                      {tWorkspace(s.permission === 'EDIT' ? 'workspace.share.perm_edit' : 'workspace.share.perm_view')}
                      {isFolder && ` · ${tWorkspace(s.includeSubfolders === false
                        ? 'workspace.share.subfolders_off' : 'workspace.share.subfolders_on')}`}
                    </span>
                  </span>
                  {canManage && (
                    <button
                      type="button"
                      className="ws-share-remove"
                      onClick={() => void handleRemove(s)}
                      disabled={busy}
                      aria-label={tWorkspace('workspace.share.remove', { name: s.targetName ?? '' })}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {/* 상위 폴더에서 받은 공유 — 해제 단추 없음(그 폴더에서 푼다). 2026-10-27 */}
            {inherited.length > 0 && (
              <>
                <h4 className="ws-share-inherited-h">{tWorkspace('workspace.share.inherited_heading')}</h4>
                <ul className="ws-share-list" aria-label={tWorkspace('workspace.share.inherited_heading')}>
                  {inherited.map((s) => (
                    <li key={`inh-${s.id}`} className="ws-share-row">
                      <span className="ws-avatar">
                        {s.targetType === 'company'
                          ? <Building2 size={13} aria-hidden />
                          : s.targetType === 'team'
                            ? <Users size={13} aria-hidden />
                            : <Key size={13} aria-hidden />}
                      </span>
                      <span className="ws-who-txt">
                        <b>{s.targetName ?? tWorkspace('workspace.detail.unknown_target')}</b>
                        <span>
                          {tWorkspace(s.permission === 'EDIT' ? 'workspace.share.perm_edit' : 'workspace.share.perm_view')}
                          {` · ${tWorkspace('workspace.share.inherited_from', { name: s.fromName })}`}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {error && <div className="workspace-modal-error" role="alert">{error}</div>}
            <footer className="workspace-modal-footer">
              <Button variant="secondary"
                onClick={onClose}
              >
                {tWorkspace('workspace.modal.btn_cancel')}
              </Button>
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}
